import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { QrCode, Share2, Download, ArrowLeft, Copy, Eye, EyeOff, Shield } from 'lucide-react';

export default function PassportView() {
  const { user, token } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchPassport();
  }, []);

  const fetchPassport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/passport`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPassport(response.data);
    } catch (error) {
      console.error('Failed to fetch passport:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/passport/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPassport(response.data);
      toast.success('Digital passport generated!');
    } catch (error) {
      toast.error('Failed to generate passport');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = () => {
    const link = `${window.location.origin}/passport/${passport.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard!');
  };

  const handleDownloadQR = () => {
    const link = document.createElement('a');
    link.href = passport.qr_code;
    link.download = 'refrx-passport-qr.png';
    link.click();
    toast.success('QR code downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading passport...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" data-testid="passport-view">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Your Digital Passport
            </h1>
            <p className="text-gray-600">Share your verified employability profile with recruiters</p>
          </div>

          {!passport ? (
            <Card className="border-0 shadow-2xl">
              <CardHeader className="text-center pb-8">
                <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <QrCode className="w-10 h-10 text-purple-600" />
                </div>
                <CardTitle>Generate Your Digital Passport</CardTitle>
                <CardDescription>
                  Create a shareable QR code and link to showcase your verified credentials
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="bg-indigo-600 hover:bg-indigo-700 px-8"
                  data-testid="generate-passport-button"
                >
                  {generating ? 'Generating...' : 'Generate Passport'}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* QR Code Card */}
              <Card className="border-0 shadow-2xl">
                <CardContent className="pt-8">
                  <div className="flex flex-col md:flex-row gap-8 items-center">
                    {/* QR Code */}
                    <div className="flex-shrink-0">
                      <div className="p-6 bg-white rounded-2xl shadow-lg border-4 border-indigo-100">
                        <img
                          src={passport.qr_code}
                          alt="Passport QR Code"
                          className="w-48 h-48"
                          data-testid="qr-code-image"
                        />
                      </div>
                    </div>

                    {/* Info */}
                    <div className="flex-1 space-y-6">
                      <div>
                        <h3 className="text-2xl font-bold mb-2">{user?.full_name}</h3>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-600" />
                          <span className="text-sm font-medium capitalize">
                            KYC: {user?.kyc_status}
                          </span>
                        </div>
                        {user?.trust_score && (
                          <div className="mt-2">
                            <span className="text-3xl font-bold gradient-text">
                              {user.trust_score.toFixed(1)}
                            </span>
                            <span className="text-gray-500 ml-1">/ 100</span>
                          </div>
                        )}
                      </div>

                      <div className="p-4 bg-gray-50 rounded-lg">
                        <Label className="text-xs text-gray-500 mb-2 block">Shareable Link</Label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={`${window.location.origin}/passport/${passport.share_token}`}
                            readOnly
                            className="flex-1 px-3 py-2 text-sm bg-white border rounded-lg"
                            data-testid="passport-link"
                          />
                          <Button
                            size="sm"
                            onClick={handleCopyLink}
                            variant="outline"
                            data-testid="copy-link-button"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          {passport.visibility === 'public' ? (
                            <Eye className="w-4 h-4 text-green-600" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-gray-600" />
                          )}
                          <Label className="cursor-pointer">Public Visibility</Label>
                        </div>
                        <Switch
                          checked={passport.visibility === 'public'}
                          disabled
                          data-testid="visibility-switch"
                        />
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={handleDownloadQR}
                          variant="outline"
                          className="flex-1"
                          data-testid="download-qr-button"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download QR
                        </Button>
                        <Button
                          onClick={handleCopyLink}
                          variant="outline"
                          className="flex-1"
                          data-testid="share-button"
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          Share Link
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Regenerate Option */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Generate New Passport</CardTitle>
                  <CardDescription>
                    Creating a new passport will invalidate the previous QR code and link
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={handleGenerate}
                    disabled={generating}
                    variant="outline"
                    data-testid="regenerate-button"
                  >
                    {generating ? 'Generating...' : 'Regenerate Passport'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}